import type { CartItem, Order, Product } from '~/services/api.server';

const isGTMActive = () => {
  if (!window.dataLayer) {
    console.warn('GTM is not defined. This could mean your google analytics script has not loaded on the page yet.');
  }
  return window.dataLayer ? true : false;
};

export const push = (data: Object) => {
  if (!isGTMActive()) return;
  return window.dataLayer.push(data);
};

const push_ecommerce = (data: Object) => {
  push({ ecommerce: null });
  push(data);
};

const getPrice = (amount: number) => {
  return amount / 100;
};

export const push_view_item = (product: Product) => {
  push_ecommerce({
    event: 'view_item',
    ecommerce: {
      currency: product.prices.currency,
      value: getPrice(product.prices.price.amount),
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          index: 0,
          item_category: product.category.name,
          price: getPrice(product.prices.price.amount),
          quantity: 1,
        },
      ],
    },
  });
};

export const push_start_design = (product: Product) => {
  push_ecommerce({
    event: 'start_design',
    ecommerce: {
      currency: product.prices.currency,
      value: getPrice(product.prices.price.amount),
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          index: 0,
          item_category: product.category.name,
          price: getPrice(product.prices.price.amount),
          quantity: 1,
        },
      ],
    },
  });
};

const push_item_list = (products: Product[], list_name: string, list_id: string) => {
  push_ecommerce({
    event: 'view_item_list',
    ecommerce: {
      item_list_id: list_id,
      item_list_name: list_name,
      items: products?.map((product, index) => {
        return {
          item_id: product.id,
          item_name: product.name,
          index,
          item_category: product.category?.name,
          price: getPrice(product.prices.price.amount),
          quantity: 1,
        };
      }),
    },
  });
};

export const push_related_products = (products: Product[]) => {
  push_item_list(products, 'Related Products', 'related_products');
};

export const push_bulk_swag_catalog = (products: Product[]) => {
  push_item_list(products, 'Bulk Swag Catalog', 'bulk_swag_catalog');
};

export const push_swag_pack_catalog = (products: Product[]) => {
  push_item_list(products, 'Swag Packs Catalog', 'swag_pack_catalog');
};

export const push_add_to_cart = (
  product: Product,
  quantity: number,
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
) => {
  const button = e.target as HTMLButtonElement;
  button.innerText === 'ADD TO CART' &&
    push_ecommerce({
      event: 'add_to_cart',
      ecommerce: {
        currency: product.prices.currency,
        value: getPrice(quantity * product.prices.price.amount),
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_category: product.category?.name,
            price: getPrice(product.prices.price.amount),
            quantity: quantity,
          },
        ],
      },
    });
};

export const push_remove_from_cart = (cart_item: CartItem) => {
  const { product, quantity } = cart_item;
  push_ecommerce({
    event: 'remove_from_cart',
    ecommerce: {
      currency: product.prices.currency,
      value: getPrice(quantity * product.prices.price.amount),
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_category: product.category?.name,
          price: getPrice(product.prices.price.amount),
          quantity: quantity,
        },
      ],
    },
  });
};

export const push_view_cart = (cart_items: CartItem[], cart_total: number) => {
  push_ecommerce({
    event: 'view_cart',
    ecommerce: {
      currency: cart_items[0]?.product.prices.currency,
      value: cart_total,
      items: cart_items.map((cart_item) => {
        const { product, quantity } = cart_item;
        return {
          item_id: product.id,
          item_name: product.name,
          item_category: product.category?.name,
          price: getPrice(product.prices.price.amount),
          quantity: quantity,
        };
      }),
    },
  });
};

export const push_purchase = (order: Order) => {
  const order_total = order.line_items.reduce((acc, item) => {
    return acc + getPrice(item.price.amount * item.quantity);
  }, 0);
  push_ecommerce({
    event: 'purchase',
    ecommerce: {
      currency: order.line_items[0].price.currency,
      transaction_id: order.id,
      value: order_total,
      items: order.line_items.map((line_item) => {
        return {
          item_id: line_item.id,
          item_name: line_item.part.product_name,
          price: getPrice(line_item.price.amount),
          quantity: line_item.quantity,
        };
      }),
    },
  });
};

export const push_sign_up = () => {
  push_ecommerce({ event: 'sign_up' });
};

export const push_login = () => {
  push_ecommerce({ event: 'login' });
};
